import { ReactElement } from 'react';

export interface IFaqType {
  expanded: boolean;
  header: IFaqHeader;
  content: ReactElement;
  id: string;
}

export interface IFaqHeader {
  centralArea: ReactElement;
}

import {
  Cancel,
  ThreeYearsCancellation,
  TwoYearsCancellation,
} from '@storefront/types/Product';
import { getCurrencySymbol } from '@storefront/utils/bundlePriceCalculator';

import { TFunction } from 'i18next';

interface TableProps {
  cancellationList?: Cancel[];
  type: string;
  t: TFunction;
}

const getCancellationTableData = (props: {
  cancellationList: Cancel[];
  t: TFunction;
}) => {
  const { cancellationList, t } = props;
  cancellationList.sort(
    (a, b) =>
      Number(a?.cancellation[1]['31D-12M']?.fee?.value) -
      Number(b?.cancellation[1]['31D-12M']?.fee?.value)
  );
  const columns = [
    {
      id: 'printer',
      label: 'Printer',
    },
    {
      id: 'upToOneYear',
      label: t('faqV2.printer.table.upToOneYear'),
    },
    {
      id: 'upToTwoYears',
      label: t('faqV2.printer.table.upToTwoYears'),
    },
    {
      id: 'upToThreeYears',
      label: t('faqV2.printer.table.upToThreeYears'),
    },
    {
      id: 'afterThreeYears',
      label: t('faqV2.printer.table.afterThreeYears'),
    },
  ];

  const data = cancellationList.map((cancel) => {
    const printer = cancel.name;

    const upToOneYear = `${getCurrencySymbol(cancel.currency)}${
      cancel?.cancellation?.[1]?.['31D-12M']?.fee?.value
    }`;
    const upToTwoYears = `${getCurrencySymbol(cancel.currency)}${
      cancel?.cancellation?.[2]?.['13M-24M']?.fee?.value
    }`;

    let upToThreeYears = '';
    let afterThreeYears = '';

    if ('25M-36M' in cancel.cancellation[3]) {
      upToThreeYears = `${getCurrencySymbol(cancel.currency)}${
        (cancel.cancellation as ThreeYearsCancellation)?.[3]?.['25M-36M']?.fee
          ?.value
      }`;
      afterThreeYears = `${getCurrencySymbol(cancel.currency)}${
        (cancel.cancellation as ThreeYearsCancellation)?.[4]?.['37M-_']?.fee
          ?.value
      }`;
    } else if ('25M-_' in cancel.cancellation[3]) {
      upToThreeYears = `${getCurrencySymbol(cancel.currency)}${
        (cancel.cancellation as TwoYearsCancellation)?.[3]?.['25M-_']?.fee
          ?.value
      }`;
      afterThreeYears = upToThreeYears;
    }
    return {
      printer,
      upToOneYear,
      upToTwoYears,
      upToThreeYears,
      afterThreeYears,
    };
  });
  return { columns, data, type: 'cancellation' };
};

const getUnreturnedPrinterFeeTableData = (props: {
  t: TFunction;
  cancellationList: Cancel[];
}) => {
  const { t, cancellationList } = props;
  cancellationList.sort(
    (a, b) =>
      Number(a?.cancellation[1]['31D-12M']?.fee?.value) -
      Number(b?.cancellation[1]['31D-12M']?.fee?.value)
  );

  const columns = [
    {
      id: 'printer',
      label: 'Printer',
    },
    {
      id: 'unreturnedPrinterFee',
      label: t('faqV2.printer.table.unreturnedPrinterFee'),
    },
  ];

  const data = cancellationList.map((cancel) => {
    const printer = cancel.name;
    const unreturnedPrinterFee = `${getCurrencySymbol(cancel.currency)}${
      cancel?.cancellation?.[1]?.['31D-12M']?.fee?.value
    }`;
    return {
      printer,
      unreturnedPrinterFee,
    };
  });
  return { columns, data, type: 'unreturnedPrinterFee' };
};

enum TableType {
  CANCELLATION = 'cancellationTable',
  UNRETURNED_PRINTER_FEE = 'unreturnedPrinterFee',
}

export const getTableData = (
  props: TableProps
): {
  columns: Array<{ id: string; label: string }>;
  data: Array<{ [key: string]: string }>;
  type: string;
} => {
  const { cancellationList, type, t } = props;

  if (type === TableType.CANCELLATION && cancellationList) {
    return getCancellationTableData({ cancellationList, t });
  }

  if (type === TableType.UNRETURNED_PRINTER_FEE && cancellationList) {
    return getUnreturnedPrinterFeeTableData({ cancellationList, t });
  }

  return { columns: [], data: [], type: '' };
};
